import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, Button, Spin } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import RouteCard from "./RouteCard";
import StoneSummary from "./StoneSummary";

// Task 2182 (F4) -- matches Diamond-PO-Prototype-v3.html's buyFromMemo()/doBuy() (lines 1096-1123)
// almost exactly: route preview, selected lines + outstanding memo shipping as line items, one
// total, one confirm button. The address resolution itself (ship-to = delivery address if still
// active, else current default) is server-side only here (plan §12.1 step 1) -- this preview uses
// the memo's own ShipToJson snapshot and the last OUT shipment's FromJson, which is what the
// server will pick in the common case; the real address is whatever PurchaseAsync actually
// resolves, same as every other modal in this module trusting the server as final authority.

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const toRouteAddress = (snap) =>
  snap && {
    Label: snap.label,
    ContactName: snap.contact,
    Address: snap.line1,
    City: snap.city,
    State: snap.state,
    Zip: snap.zip,
    Country: snap.country,
    Phone: snap.phone,
  };

export const BuyModal = ({ memoId, lineIds, onClose, onDone }) => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null);
  const [lines, setLines] = useState([]);
  const [charges, setCharges] = useState([]);
  const [lastOutShipment, setLastOutShipment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!memoId || !dealerId) return;
    setLoading(true);
    PurchaseOrderService.GetDiamondDoc(memoId, { DealerID: String(dealerId) })
      .then((response) => {
        setLoading(false);
        if (response.data.status === true) {
          const d = response.data.responseData;
          setDoc(d.Doc);
          setLines((d.Lines || []).filter((l) => lineIds.includes(l.DiamondLineID) && l.StatusCode === "OUT"));
          const outShipments = (d.Shipments || []).filter((s) => s.Direction === "OUT");
          setCharges(outShipments.filter((s) => s.BillTo === "RETAILER" && Number(s.ShippingCost) > 0 && !s.InvoicedOnDocID));
          // Preview only -- which SHIPPING address the supplier actually used, regardless of
          // whether that shipment still has unbilled cost. The real address PurchaseAsync picks
          // (plan §12.1) is resolved server-side either way.
          setLastOutShipment(outShipments[outShipments.length - 1] || null);
        } else {
          NotificationManager.error(t(response.data.message || "Could not load this memo"));
        }
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not load this memo"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoId, dealerId]);

  if (!memoId) return null;
  if (loading) {
    return (
      <Modal visible onCancel={onClose} footer={null} title={t("Buy off memo")}>
        <Spin spinning style={{ display: "block", padding: 40 }} />
      </Modal>
    );
  }
  if (!doc || lines.length === 0) return null;

  const shipToSnap = typeof doc.ShipToJson === "string" ? JSON.parse(doc.ShipToJson) : doc.ShipToJson;
  const lastOutFrom = lastOutShipment
    ? typeof lastOutShipment.FromJson === "string"
      ? JSON.parse(lastOutShipment.FromJson)
      : lastOutShipment.FromJson
    : null;

  const linesTotal = lines.reduce((sum, l) => sum + Number(l.LineTotal || 0), 0);
  const chargesTotal = charges.reduce((sum, s) => sum + Number(s.ShippingCost || 0), 0);
  const grandTotal = linesTotal + chargesTotal;

  const handleSubmit = () => {
    setSubmitting(true);
    PurchaseOrderService.PurchaseDiamondDoc(memoId, { DealerID: String(dealerId), LineIds: lineIds })
      .then((response) => {
        setSubmitting(false);
        if (response.data.status === true) {
          NotificationManager.success(t("Created {{num}} — stones already in hand", { num: response.data.responseData?.DocNumber || "" }));
          onDone && onDone();
          onClose();
        } else {
          NotificationManager.error(t(response.data.message || "Could not create the purchase order"));
        }
      })
      .catch(() => {
        setSubmitting(false);
        NotificationManager.error(t("Could not create the purchase order"));
      });
  };

  return (
    <Modal
      visible
      onCancel={onClose}
      width={640}
      title={t("Buy {{n}} stone(s) off {{num}}", { n: lines.length, num: doc.DocNumber })}
      footer={[
        <Button key="cancel" onClick={onClose}>{t("Cancel")}</Button>,
        <Button key="go" type="primary" loading={submitting} onClick={handleSubmit}>
          {t("Create purchase order — {{total}}", { total: fmtMoney(grandTotal) })}
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 14 }}>
        <RouteCard
          fromAddress={toRouteAddress(lastOutFrom)}
          toAddress={toRouteAddress(shipToSnap)}
          fromPartyName={doc.SupplierName}
          toPartyName={doc.RetailerName}
        />
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 10 }}>
        <tbody>
          {lines.map((l) => {
            const snap = typeof l.StoneSnapshotJson === "string" ? JSON.parse(l.StoneSnapshotJson) : l.StoneSnapshotJson;
            return (
              <tr key={l.DiamondLineID} style={{ borderBottom: "1px solid #f3f3f3" }}>
                <td style={{ padding: "6px 0" }}>
                  <StoneSummary snap={snap} />
                </td>
                <td style={{ padding: "6px 0", textAlign: "right" }}>{fmtMoney(l.LineTotal)}</td>
              </tr>
            );
          })}
          {charges.map((s) => (
            <tr key={s.ShipmentID} style={{ borderBottom: "1px solid #f3f3f3" }}>
              <td style={{ padding: "6px 0" }}>
                {t("Memo shipping")} · {s.TrackingNo}
                <div style={{ fontSize: 11, color: "#79838f" }}>{t("charged to you when the memo shipped")}</div>
              </td>
              <td style={{ padding: "6px 0", textAlign: "right" }}>{fmtMoney(s.ShippingCost)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ padding: "8px 0", fontWeight: 700 }}>{t("Purchase order total")}</td>
            <td style={{ padding: "8px 0", textAlign: "right", fontWeight: 700 }}>{fmtMoney(grandTotal)}</td>
          </tr>
        </tbody>
      </table>

      <div style={{ fontSize: 12, color: "#79838f" }}>
        {t("The stones are already in your safe, so nothing ships. Title passes now and the order is issued as an invoice.")}
      </div>
    </Modal>
  );
};

export default BuyModal;
